import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DragDropFileInput } from '@/components/demand-example/DragDropFileInput';
import { FirmCombobox } from '@/components/firms/FirmCombobox';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { cmsImportsApiClient } from '@/services/api/cmsImportsService';

// CMS type options
const CMS_OPTIONS = [
  { value: 'casepeer', label: 'CasePeer' },
  { value: 'clio', label: 'Clio' },
  { value: 'filevine', label: 'Filevine' },
  { value: 'mycase', label: 'MyCase' },
  { value: 'neos', label: 'Neos' },
  { value: 'litify', label: 'Litify' },
  { value: 'smartadvocate', label: 'SmartAdvocate' },
] as const;

type CmsType = (typeof CMS_OPTIONS)[number]['value'];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  defaultFirmId?: string;
}

export const CMSImportUploadDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  defaultFirmId,
}) => {
  const navigate = useNavigate();
  const [createImport, { isLoading, error }] =
    cmsImportsApiClient.useCmsImportCreateMutation();
  const [firmId, setFirmId] = useState<string>(defaultFirmId ?? '');
  const [cmsType, setCmsType] = useState<CmsType>('casepeer');
  const [file, setFile] = useState<File | null>(null);

  const selectedCmsLabel =
    CMS_OPTIONS.find((opt) => opt.value === cmsType)?.label || 'CMS';

  const handleSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    if (!firmId || !file) return;

    try {
      const result = await createImport({
        body: {
          firm_id: firmId,
          cms_type: cmsType,
          file,
        },
      }).unwrap();
      onClose();
      // Navigate to the new import's detail page
      navigate(`/profile/cms-import/${result.id}`);
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} size="lg">
      <form onSubmit={handleSubmit}>
        <DialogBody>
          <DialogTitle>Import Tasks from CMS</DialogTitle>

          {error && (
            <div className="mt-4 rounded-md bg-red-50 dark:bg-red-900/20 p-4">
              <p className="text-sm text-red-800 dark:text-red-200">
                <strong>Upload failed:</strong>{' '}
                {'data' in error && error.data
                  ? typeof error.data === 'object' && 'detail' in error.data
                    ? String(error.data.detail)
                    : JSON.stringify(error.data)
                  : 'status' in error
                    ? `Error ${error.status}`
                    : 'An unexpected error occurred'}
              </p>
            </div>
          )}

          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
            Upload a JSON export from your CMS containing tasks to import into
            Finch.
          </p>

          <div className="mt-6 space-y-5">
            {/* CMS Type Selector */}
            <div>
              <div className="block text-sm font-medium mb-1.5 text-zinc-900 dark:text-white">
                CMS Type
              </div>
              <div className="relative">
                <select
                  value={cmsType}
                  onChange={(e) => setCmsType(e.target.value as CmsType)}
                  className="w-full appearance-none rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 pr-10 text-sm text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {CMS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronUpDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
              </div>
            </div>

            {/* Firm Selector */}
            <div>
              <div className="block text-sm font-medium mb-1.5 text-zinc-900 dark:text-white">
                Select Firm
              </div>
              <FirmCombobox
                value={firmId}
                onChange={setFirmId}
                className="w-full"
                panelClassName="z-50"
              />
            </div>

            {/* File Upload */}
            <div>
              <div className="block text-sm font-medium mb-1.5 text-zinc-900 dark:text-white">
                {selectedCmsLabel} Export File
              </div>
              <DragDropFileInput
                selectedFile={file}
                onFileChange={setFile}
                accept=".json"
                idleTitle="Upload JSON file"
                draggingTitle="Drop JSON here"
                subtitle={`Drag and drop a ${selectedCmsLabel} JSON export, or click to browse`}
              />
            </div>
          </div>

          <DialogActions>
            <Button color="zinc" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="blue"
              type="submit"
              disabled={!firmId || !file || isLoading}
            >
              {isLoading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogActions>
        </DialogBody>
      </form>
    </Dialog>
  );
};
